import axios, { type AxiosError } from "axios";
import { useAppStore } from "@/store/appStore";
import { runtimeEnv } from "@/utils/runtimeEnv";

const UPSTREAM_FAILURE_THRESHOLD = 2;
let consecutiveUpstreamFailures = 0;

const apiClient = axios.create({
  baseURL: runtimeEnv.VITE_API_BASE_URL
    ? `${runtimeEnv.VITE_API_BASE_URL}/api`
    : "/api",
  withCredentials: true, // Required for cookie-based auth
});

// A JSON request that resolves with an HTML document did not reach the API:
// the SWA navigation fallback, an App Service error shell, or an IdP login page
// reached after a transparently-followed auth redirect all answer 200 with
// markup. Treat that as a failed request instead of letting callers destructure
// a string as if it were a DTO.
function isHtmlBody(response: { config?: { responseType?: string }; data?: unknown }): boolean {
  const responseType = response.config?.responseType;
  if (responseType && responseType !== "json") return false;
  return (
    typeof response.data === "string" &&
    /^\s*(<!doctype html|<html\b)/i.test(response.data)
  );
}

// Response interceptor — handle errors and redirect on 401
apiClient.interceptors.response.use(
  (response) => {
    consecutiveUpstreamFailures = 0;
    if (isHtmlBody(response)) {
      return Promise.reject(
        new Error(
          `Expected JSON from ${response.config?.url ?? "API"} but received an HTML document`,
        ),
      );
    }
    return response;
  },
  (error: AxiosError) => {
    if (import.meta.env.DEV) {
      console.error(
        `[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url} → ${error.response?.status}`,
        error.response?.data ?? error.message,
      );
    }

    // On 401 Unauthorized, redirect to login (unless already on identity endpoint)
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url ?? "";

      // Don't redirect on /current-user — loadIdentity handles 401 itself
      if (!requestUrl.includes("/current-user")) {
        // Do not redirect while the browser is offline.
        // A 401 in this state means a cached/stale response surfaced —
        // it is not a genuine session expiry we can fix by going to the IdP.
        if (!navigator.onLine) {
          return Promise.reject(error);
        }

        console.warn("[Auth] 401 Unauthorized - Redirecting to login...");

        // Store current location for post-login redirect (include search params so
        // e.g. /search?q=foo survives the re-auth round-trip)
        const returnUrl = window.location.pathname + window.location.search;

        const apiBaseUrl = runtimeEnv.VITE_API_BASE_URL
          ? `${runtimeEnv.VITE_API_BASE_URL}/api`
          : "/api";

        // Redirect to login endpoint
        const isDevelopment = import.meta.env.DEV;
        if (isDevelopment) {
          window.location.href = `${apiBaseUrl}/auth/fake-login`;
        } else {
          window.location.href = `${apiBaseUrl}/saml/login?returnUrl=${encodeURIComponent(returnUrl)}`;
        }
      }
    }

    // silently ignore missing SVG/PNG media (e.g. optional icons), but let PDF errors propagate
    const mediaUrl = error.config?.url ?? "";
    if (mediaUrl.includes("/media") && /\.(svg|png)(\?|$)/i.test(mediaUrl)) {
      return;
    }

    // Passive upstream detection: a missing response with navigator.onLine=true
    // means the request never reached the server — upstream connectivity is lost.
    // Require two consecutive failures before marking offline to avoid false
    // positives from momentary blips or an API server restart mid-development.
    if (!error.response && navigator.onLine) {
      consecutiveUpstreamFailures++;
      if (consecutiveUpstreamFailures >= UPSTREAM_FAILURE_THRESHOLD) {
        consecutiveUpstreamFailures = 0;
        useAppStore.getState().setTrulyOffline(true);
      }
    } else {
      // Any HTTP response (even an error status) means we reached the server.
      consecutiveUpstreamFailures = 0;
    }

    return Promise.reject(error);
  },
);

export default apiClient;
